import {Slider, SliderProps} from '@mui/material';
import {MuiSliderEmotionLtrProvider} from './MuiSliderEmotionLtrProvider';

const DEFAULT_SINGLE_SX = {
  color: '#0080A6',
  '& .MuiSlider-track': {height: 4, border: 'none'},
  '& .MuiSlider-rail': {height: 4, opacity: 1, backgroundColor: '#E6E0E9'},
  '& .MuiSlider-thumb': {width: 20, height: 20},
  // Disabled: theme grays out via opacity + child colors; override on root (&.Mui-disabled), not & .Mui-disabled
  '&.Mui-disabled': {
    color: '#0080A6',
    opacity: 1,
    '& .MuiSlider-track': {
      backgroundColor: '#0080A6',
      border: 'none',
    },
    '& .MuiSlider-rail': {opacity: 1, backgroundColor: '#E6E0E9'},
    '& .MuiSlider-thumb': {backgroundColor: '#0080A6'},
    '& .MuiSlider-valueLabel': {
      backgroundColor: '#0080A6',
      color: '#fff',
      opacity: 1,
    },
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: '#0080A6',
    color: '#fff',
    fontSize: 12,
    fontWeight: 500,
    padding: '6px 10px',
    borderRadius: '50%',
  },
} as const;

type AppSingleSliderProps = Omit<SliderProps, 'sx'> & {
  /** Content rendered to the left of the slider (start / min side). */
  startLabel?: React.ReactNode;
  /** Content rendered to the right of the slider (end / max side). */
  endLabel?: React.ReactNode;
  /** Extra className applied to the outer wrapper div. */
  containerClassName?: string;
  /** Extra className applied to both label spans. */
  labelClassName?: string;
  /** Merged on top of the default sx. Supports all MUI sx formats. */
  sx?: SliderProps['sx'];
};

function AppSingleSlider({
  startLabel,
  endLabel,
  containerClassName = '',
  labelClassName = 'shrink-0 text-textColor tabular-nums',
  sx,
  size = 'medium',
  valueLabelDisplay = 'on',
  ...sliderProps
}: AppSingleSliderProps) {
  // Merge default sx with any consumer overrides.
  // Using the MUI array form so both objects are deep-merged by the theme engine.
  const mergedSx = sx
    ? [DEFAULT_SINGLE_SX, ...(Array.isArray(sx) ? sx : [sx])]
    : DEFAULT_SINGLE_SX;

  return (
    <div dir="ltr" className={`flex items-center gap-3 ${containerClassName}`}>
      {startLabel !== undefined && (
        <span className={labelClassName}>{startLabel}</span>
      )}

      <MuiSliderEmotionLtrProvider>
        <Slider
          size={size}
          valueLabelDisplay={valueLabelDisplay}
          {...sliderProps}
          sx={mergedSx}
        />
      </MuiSliderEmotionLtrProvider>

      {endLabel !== undefined && (
        <span className={labelClassName}>{endLabel}</span>
      )}
    </div>
  );
}

export default AppSingleSlider;
